// Mercapoli - Admin Rideres List
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Bike, Search, Loader2, Plus, Edit, 
  ToggleLeft, ToggleRight, MapPin, Star, Package, Trash2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { admin as adminApi, config as configApi } from '../../lib/api';
import { toast } from 'sonner';

const AdminRideres = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rideres, setRideres] = useState([]);
  const [paises, setPaises] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [paisFiltro, setPaisFiltro] = useState('all');
  const [estadoFiltro, setEstadoFiltro] = useState('all');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedRider, setSelectedRider] = useState(null);

  useEffect(() => {
    loadData();
  }, [paisFiltro, estadoFiltro]);

  const loadData = async () => {
    try {
      // Cargar países
      const paisesData = await configApi.getPaises();
      setPaises(paisesData.paises);
      
      // Cargar rideres con filtros
      const params = {};
      if (paisFiltro !== 'all') params.pais = paisFiltro;
      if (estadoFiltro !== 'all') params.activo = estadoFiltro === 'activo';
      
      const data = await adminApi.getRiders(params);
      setRideres(data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar rideres');
    }
    setLoading(false);
  };

  const toggleEstado = async (userId, estadoActual) => {
    try {
      await adminApi.updateRider(userId, { activo: !estadoActual });
      toast.success(`Rider ${!estadoActual ? 'activado' : 'desactivado'}`);
      loadData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleDelete = (rider) => {
    setSelectedRider(rider);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!selectedRider) return;
    
    try {
      await adminApi.deleteRider(selectedRider.user_id);
      toast.success('Rider eliminado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar rider');
    }
    setShowDeleteDialog(false);
    setSelectedRider(null);
  };

  const filtered = rideres.filter(r =>
    !filtro || 
    r.nombre?.toLowerCase().includes(filtro.toLowerCase()) ||
    r.email?.toLowerCase().includes(filtro.toLowerCase()) ||
    r.ciudad?.toLowerCase().includes(filtro.toLowerCase())
  );

  const getVehiculoIcon = (vehiculo) => {
    const icons = {
      moto: '🏍️',
      bicicleta: '🚲',
      auto: '🚗'
    };
    return icons[vehiculo] || '🏍️';
  };

  const getPaisNombre = (codigo) => {
    const pais = paises.find(p => p.codigo === codigo);
    return pais?.nombre || codigo;
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin')} className="p-2 -ml-2 rounded-lg hover:bg-muted">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="font-heading text-lg font-bold">Rideres</h1>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{rideres.length} rideres</Badge>
              <Button 
                onClick={() => navigate('/admin/repartidores/nuevo')}
                className="bg-brand-primary hover:bg-brand-secondary"
                data-testid="new-rider-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nuevo
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container-app py-6">
        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, email o ciudad..."
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              className="pl-10"
              data-testid="search-rideres"
            />
          </div>
          
          <Select value={paisFiltro} onValueChange={setPaisFiltro}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder="País" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los países</SelectItem>
              {paises.map(p => (
                <SelectItem key={p.codigo} value={p.codigo}>{p.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={estadoFiltro} onValueChange={setEstadoFiltro}>
            <SelectTrigger className="w-full sm:w-32">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="activo">Activos</SelectItem>
              <SelectItem value="inactivo">Inactivos</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <Bike className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No se encontraron rideres</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => navigate('/admin/repartidores/nuevo')}
            >
              <Plus className="h-4 w-4 mr-2" />
              Agregar primer rider
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((rep) => (
              <Card key={rep.user_id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-brand-primary/10 flex items-center justify-center text-2xl">
                        {getVehiculoIcon(rep.vehiculo)}
                      </div>
                      <div>
                        <h3 className="font-medium">{rep.nombre}</h3>
                        <p className="text-sm text-muted-foreground">{rep.email}</p>
                      </div>
                    </div>
                    <Badge className={rep.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {rep.activo ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      <span>{rep.ciudad}, {getPaisNombre(rep.pais)}</span>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        <span>{rep.calificacion?.toFixed(1) || '5.0'}</span>
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Package className="h-4 w-4" />
                        <span>{rep.total_entregas || 0} entregas</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-muted-foreground">
                      <span>📱 {rep.telefono}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => navigate(`/admin/repartidores/editar/${rep.user_id}`)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Editar
                    </Button>
                    <button
                      onClick={() => toggleEstado(rep.user_id, rep.activo)}
                      className={`p-2 rounded-lg transition-colors ${
                        rep.activo 
                          ? 'text-brand-accent hover:bg-brand-accent/10' 
                          : 'text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      {rep.activo ? (
                        <ToggleRight className="h-6 w-6" />
                      ) : (
                        <ToggleLeft className="h-6 w-6" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDelete(rep)}
                      className="p-2 rounded-lg text-red-500 hover:bg-red-50"
                      title="Eliminar rider"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Dialog Confirmar Eliminación */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar rider?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción desactivará al rider "{selectedRider?.nombre}". 
              No podrá recibir más pedidos hasta ser reactivado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminRideres;
